import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { addDoc, collection, doc, getDoc, getDocs, orderBy, query, Timestamp } from "firebase/firestore";
import { useHistory, useParams } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import CabecalhoDetalhes from "../Components/CabecalhoDetalhes/CabecalhoDetalhes";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { alertaSistema } from "../Components/PopupSistema/PopupSistema";
import { auth, db } from "../Config/firebase";
import { ENSAIOS_LABORATORIO } from "../CadastroSolicitacaoServicos/solicitacaoServicosModel";
import PropostaPrint from "./RelatorioPropostaComercialPrint";
import {
  calcularTotais,
  descricaoServico,
  linhaTotal,
  moeda,
  numerarItensProposta,
  propostaComDefaults,
  textoValidadeProposta,
  tiposServicosPadraoProposta,
  valorMonetarioPorExtenso,
} from "./propostaComercialModel";
import "./CadastroPropostaComercial.css";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const tipoServicoInicial = {
  nome: "",
  norma: "",
  tipo: "ENSAIO",
  categoria: "",
};

function formatarDataCabecalho(valor) {
  if (!valor) return "-";
  const data = valor?.toDate?.() || new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(valor)) ? `${valor}T00:00:00` : valor);
  return Number.isNaN(data.getTime()) ? "-" : data.toLocaleDateString("pt-BR");
}

function servicoVazio() {
  return {
    id: `servico-${Date.now()}`,
    servicoId: "",
    unidade: "",
    quantidade: "",
    descricao: "",
    detalhesServico: "",
    norma: "",
    valorUnitario: "",
  };
}

function grupoVazio(numeroGrupo) {
  return {
    id: `grupo-${Date.now()}`,
    titulo: `Grupo ${numeroGrupo} - Serviços`,
    subtitulo: "Valores dos serviços contratados",
    exibirSubtotal: true,
    itens: [servicoVazio()],
  };
}

function normalizarServico(valor = {}) {
  return {
    ...valor,
    rotulo: descricaoServico(valor),
  };
}

const ensaiosLaboratorioProposta = ENSAIOS_LABORATORIO.flatMap((ensaio) => [
  ensaio,
  ...(ensaio.subEnsaios || []),
]).map((ensaio) => ({
  ...ensaio,
  tipo: ensaio.tipo || "Ensaio",
}));

function textoComparacaoServico(servico = {}) {
  return String([servico.nome, servico.norma, servico.categoria].filter(Boolean).join(" "))
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function chaveEnsaioEquivalente(servico = {}) {
  const texto = textoComparacaoServico(servico);

  if (/granulometr/.test(texto) && /asfalto/.test(texto)) return "granulometria-asfalto";
  if (/\braa\b/.test(texto) || /reatividade potencial/.test(texto)) return "raa";
  if (/granulometr/.test(texto)) return "granulometria";
  if (/densidade/.test(texto) && /absorcao/.test(texto)) return "densidade-absorcao";
  if (/massa unitaria/.test(texto)) return "massa-unitaria";
  if (/equivalente de areia/.test(texto)) return "equivalente-areia";
  if (/indice de forma/.test(texto)) return "indice-forma";
  if (/los angeles/.test(texto)) return "abrasao-los-angeles";
  return "";
}

function montarServicosSemRepeticao(tiposServicos = []) {
  const descricoesIncluidas = new Set();
  const servicosDaProposta = [...tiposServicos, ...tiposServicosPadraoProposta].filter((servico) => {
    const descricao = textoComparacaoServico(servico);
    if (!descricao || descricoesIncluidas.has(descricao)) return false;
    descricoesIncluidas.add(descricao);
    return true;
  });
  const chavesDaProposta = new Set(servicosDaProposta.map(chaveEnsaioEquivalente).filter(Boolean));

  const ensaiosComplementares = ensaiosLaboratorioProposta.filter((ensaio) => {
    const descricao = textoComparacaoServico(ensaio);
    const chave = chaveEnsaioEquivalente(ensaio);
    if (descricoesIncluidas.has(descricao) || (chave && chavesDaProposta.has(chave))) return false;
    descricoesIncluidas.add(descricao);
    return true;
  });

  return [...servicosDaProposta, ...ensaiosComplementares];
}

function ServicoAutocompleteDebounced({
  item,
  opcoesServico,
  onDescricaoChange,
  onServicoSelect,
}) {
  const [texto, setTexto] = useState(item.descricao || "");
  const [termoBusca, setTermoBusca] = useState(item.descricao || "");
  const [editandoDescricao, setEditandoDescricao] = useState(!item.descricao);
  const onDescricaoChangeRef = useRef(onDescricaoChange);

  useEffect(() => {
    onDescricaoChangeRef.current = onDescricaoChange;
  }, [onDescricaoChange]);

  useEffect(() => {
    setTexto(item.descricao || "");
    setTermoBusca(item.descricao || "");
  }, [item.descricao]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTermoBusca(texto);
      onDescricaoChangeRef.current(texto);
    }, 1000);

    return () => clearTimeout(timer);
  }, [texto]);

  const termoNormalizado = String(termoBusca || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const opcoesFiltradas = termoNormalizado
    ? opcoesServico.filter((servico) =>
        String(servico.rotulo || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .includes(termoNormalizado)
      )
    : opcoesServico;

  const servicoAtual = opcoesServico.find((servico) =>
    (item.servicoId && servico.id === item.servicoId) ||
    descricaoServico(servico) === item.descricao
  ) || null;

  if (texto && !editandoDescricao) {
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={() => {
          setTermoBusca("");
          setEditandoDescricao(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setTermoBusca("");
            setEditandoDescricao(true);
          }
        }}
        sx={{
          minHeight: 40,
          px: 1.5,
          py: 1,
          border: "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          bgcolor: "background.paper",
          cursor: "text",
          display: "flex",
          alignItems: "center",
          "&:hover": { borderColor: "text.primary" },
        }}
      >
        <Typography sx={{ lineHeight: 1.35, whiteSpace: "normal", overflowWrap: "anywhere" }}>
          {texto}
        </Typography>
      </Box>
    );
  }

  return (
    <Autocomplete
      freeSolo
      openOnFocus
      options={opcoesFiltradas}
      filterOptions={(options) => options}
      getOptionLabel={(option) => typeof option === "string" ? option : option.rotulo || descricaoServico(option)}
      value={servicoAtual}
      inputValue={texto}
      onInputChange={(event, value) => setTexto(value)}
      onChange={(event, value) => {
        if (typeof value === "string") {
          setTexto(value);
          onDescricaoChange(value);
          setEditandoDescricao(false);
          return;
        }

        onServicoSelect(value);
        setTexto(value ? descricaoServico(value) : "");
        setEditandoDescricao(!value);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          size="small"
          placeholder="Selecione ou digite um serviço"
          onFocus={() => setTermoBusca("")}
        />
      )}
    />
  );
}

function ValorUnitarioInput({ value, onChange }) {
  const [valorLocal, setValorLocal] = useState(value || "");

  useEffect(() => {
    setValorLocal(value || "");
  }, [value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (valorLocal !== (value || "")) {
        onChange(valorLocal);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [valorLocal, onChange, value]);

  return (
    <TextField
      size="small"
      fullWidth
      value={valorLocal}
      onChange={(event) => setValorLocal(event.target.value)}
      onBlur={() => onChange(valorLocal)}
    />
  );
}

export default function DetalhesPropostaComercial() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const { propostaId } = useParams();
  const history = useHistory();
  const [proposta, setProposta] = useState(null);
  const [tiposServicos, setTiposServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [modoPrint, setModoPrint] = useState(false);
  const [openNovoServico, setOpenNovoServico] = useState(false);
  const [novoServico, setNovoServico] = useState(tipoServicoInicial);
  const [detalhesAbertos, setDetalhesAbertos] = useState({});
const detalhesRefs = useRef({});
  const propostaOriginalRef = useRef(null);
  const totais = useMemo(() => calcularTotais(proposta || {}), [proposta]);
  const alterado = useMemo(() => {
    if (!proposta || !propostaOriginalRef.current) return false;
    return JSON.stringify(proposta) !== JSON.stringify(propostaOriginalRef.current);
  }, [proposta]);

  useEffect(() => {
    carregarDados();
  }, [propostaId]);

  async function carregarDados() {
    setLoading(true);
    const [propostaSnap, tiposSnap] = await Promise.all([
      getDoc(doc(db, "tblPropostasComerciais", propostaId)),
      getDocs(consultaTenantAtual(query(collection(db, "tblTiposServicosProposta"), orderBy("nome")))),
    ]);

    if (!propostaSnap.exists()) {
      await alertaSistema("Proposta não encontrada.");
      history.push("/app/proposta-comercial");
      return;
    }

    const propostaCarregada = numerarItensProposta(
      propostaComDefaults({ id: propostaSnap.id, ...propostaSnap.data() })
    );
    setProposta(propostaCarregada);
    propostaOriginalRef.current = propostaCarregada;
    setTiposServicos(tiposSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setLoading(false);
  }

  const opcoesServico = useMemo(
    () => montarServicosSemRepeticao(tiposServicos).map(normalizarServico),
    [tiposServicos]
  );

  function atualizarProposta(campo, valor) {
    setProposta((atual) => ({ ...atual, [campo]: valor }));
  }

  function visualizarModelo(modeloRelatorio) {
    setProposta((atual) => ({ ...atual, modeloRelatorio }));
    setModoPrint(true);
  }

  function atualizarTexto(campo, valor) {
    setProposta((atual) => ({ ...atual, textos: { ...atual.textos, [campo]: valor } }));
  }

function atualizarValidadeProposta(valor) {
  const somenteNumeros = String(valor || "").replace(/\D/g, "");

  setProposta((atual) => ({
    ...atual,
    validadeProposta: somenteNumeros,
    textos: {
      ...atual.textos,
      validade: textoValidadeProposta(somenteNumeros),
    },
  }));
}

  function atualizarGrupo(indiceGrupo, campo, valor) {
    setProposta((atual) => ({
      ...atual,
      tabelas: atual.tabelas.map((grupo, index) =>
        index === indiceGrupo ? { ...grupo, [campo]: valor } : grupo
      ),
    }));
  }

  function atualizarItem(indiceGrupo, indiceItem, campo, valor) {
    setProposta((atual) => ({
      ...atual,
      tabelas: atual.tabelas.map((grupo, grupoIndex) =>
        grupoIndex === indiceGrupo
          ? {
              ...grupo,
              itens: grupo.itens.map((item, itemIndex) =>
                itemIndex === indiceItem ? { ...item, [campo]: valor } : item
              ),
            }
          : grupo
      ),
    }));
  }

  function chaveDetalheServico(indiceGrupo, indiceItem) {
  return `${indiceGrupo}-${indiceItem}`;
}

function abrirDetalheServico(indiceGrupo, indiceItem) {
  const chave = chaveDetalheServico(indiceGrupo, indiceItem);

  setDetalhesAbertos((atual) => ({
    ...atual,
    [chave]: true,
  }));

  setTimeout(() => {
    detalhesRefs.current[chave]?.focus();
  }, 100);
}

  function selecionarServico(indiceGrupo, indiceItem, servico) {
    if (!servico) {
      atualizarItem(indiceGrupo, indiceItem, "servicoId", "");
      return;
    }

    setProposta((atual) => numerarItensProposta({
      ...atual,
      tabelas: atual.tabelas.map((grupo, grupoIndex) =>
        grupoIndex === indiceGrupo
          ? {
              ...grupo,
              itens: grupo.itens.map((item, itemIndex) =>
                itemIndex === indiceItem
                  ? {
                      ...item,
                      servicoId: servico.id || "",
                      descricao: descricaoServico(servico),
                      norma: servico.norma || "",
                    }
                  : item
              ),
            }
          : grupo
      ),
    }));
  }

  function adicionarItem(indiceGrupo) {
    setProposta((atual) => numerarItensProposta({
      ...atual,
      tabelas: atual.tabelas.map((grupo, grupoIndex) =>
        grupoIndex === indiceGrupo
          ? { ...grupo, itens: [...grupo.itens, servicoVazio()] }
          : grupo
      ),
    }));
  }

  function removerItem(indiceGrupo, indiceItem) {
    setProposta((atual) => numerarItensProposta({
      ...atual,
      tabelas: atual.tabelas.map((grupo, grupoIndex) =>
        grupoIndex === indiceGrupo
          ? { ...grupo, itens: grupo.itens.filter((_, itemIndex) => itemIndex !== indiceItem) }
          : grupo
      ),
    }));
  }

  function adicionarGrupo() {
    setProposta((atual) => numerarItensProposta({
      ...atual,
      tabelas: [...(atual.tabelas || []), grupoVazio((atual.tabelas || []).length + 1)],
    }));
  }

  async function salvarProposta() {
    if (!alterado || salvando) return;
    if ((proposta.tabelas || []).some((grupo) => (grupo.itens || []).some((item) => !String(item.unidade || "").trim()))) {
      await alertaSistema("Selecione a unidade de todos os serviços antes de salvar.");
      return;
    }
    setSalvando(true);
    let salvouComSucesso = false;

    try {
      const usuarioAtual = auth.currentUser;
      const dados = await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblPropostasComerciais", propostaId),
        original: propostaOriginalRef.current,
        atual: proposta,
        preparar: (estado) => ({
          ...numerarItensProposta(estado),
          revisao: "00",
          rq: "RQ 6.4",
          descontoPercentual: estado.descontoPercentual || "",
          totalProposta: calcularTotais(estado).total,
        }),
        metadados: {
          atualizadoEm: Timestamp.now(),
          atualizadoPor: usuarioAtual?.uid || null,
          atualizadoPorEmail: usuarioAtual?.email || "",
          atualizadoPorNome: usuarioAtual?.displayName || usuarioAtual?.email || "",
        },
      });
      const propostaSalva = { ...dados, id: propostaId };
      setProposta(propostaSalva);
      propostaOriginalRef.current = propostaSalva;
      salvouComSucesso = true;
    } finally {
      setSalvando(false);
    }

    if (salvouComSucesso) {
      await alertaSistema("Proposta comercial salva com sucesso.");
    }
  }

  async function salvarNovoServico() {
    if (!novoServico.nome.trim()) {
      alert("Informe o nome do serviço.");
      return;
    }

    const tipo = String(novoServico.tipo || "").trim().toUpperCase();
    const categoria = String(novoServico.categoria || "").trim().toUpperCase();
    const ref = await addDoc(collection(db, "tblTiposServicosProposta"), aplicarTenantAtual({
      ...novoServico,
      nome: novoServico.nome.trim(),
      norma: novoServico.norma.trim(),
      tipo,
      categoria,
      criadoEm: Timestamp.now(),
    }));

    const salvo = { id: ref.id, ...novoServico, tipo, categoria };
    setTiposServicos((atuais) => [...atuais, salvo].sort((a, b) => String(a.nome).localeCompare(String(b.nome))));
    setNovoServico(tipoServicoInicial);
    setOpenNovoServico(false);
  }

  if (loading || !proposta) {
    return (
      <>
        <Navbar />
        <Box className="proposta-page">
          <Typography>Carregando proposta...</Typography>
        </Box>
      </>
    );
  }

  if (modoPrint) {
    return (
      <PropostaPrint
        proposta={proposta}
        onVoltar={() => setModoPrint(false)}
      />
    );
  }

  return (
    <>
      <Navbar />
      <Box className="proposta-page">
        <CabecalhoDetalhes
          titulo="Descrição da proposta comercial"
          subtitulo={`${proposta.rq || "RQ 6.4"}   Rev.${proposta.revisao || "00"} - ${formatarDataCabecalho(proposta.data)}`}
        >
  <Button
    variant="outlined"
    onClick={() => history.push("/app/proposta-comercial")}
    sx={{ alignSelf: "flex-end", height: 40 }}
  >
    Voltar
  </Button>

  <Stack spacing={0.25} alignItems="center">
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
      Visualizar
    </Typography>
    <ButtonGroup aria-label="Visualizar modelo do relatório da proposta" sx={{ height: 40 }}>
      <Button
        startIcon={<VisibilityIcon />}
        variant={proposta.modeloRelatorio !== "modelo2" ? "contained" : "outlined"}
        onClick={() => visualizarModelo("modelo1")}
      >
        Modelo 1
      </Button>
      <Button
        startIcon={<VisibilityIcon />}
        variant={proposta.modeloRelatorio === "modelo2" ? "contained" : "outlined"}
        onClick={() => visualizarModelo("modelo2")}
      >
        Modelo 2
      </Button>
    </ButtonGroup>
  </Stack>

  <Button
    variant="contained"
    startIcon={<SaveIcon />}
    onClick={salvarProposta}
    disabled={salvando || !alterado}
    sx={{ alignSelf: "flex-end", height: 40 }}
  >
    {salvando ? "Salvando..." : "Salvar"}
  </Button>
        </CabecalhoDetalhes>

        <Paper className="proposta-form-tabela" variant="outlined">
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Código" value={proposta.codigo} onChange={(event) => atualizarProposta("codigo", event.target.value)} sx={{ width: { xs: "100%", md: 220 }, flexShrink: 0 }} />
              <TextField label="Data" type="date" InputLabelProps={{ shrink: true }} value={proposta.data} onChange={(event) => atualizarProposta("data", event.target.value)} sx={{ width: { xs: "100%", md: 180 }, flexShrink: 0 }} />
              <TextField
  label="Validade"
  type="number"
  value={proposta.validadeProposta || ""}
  onChange={(event) => atualizarValidadeProposta(event.target.value)}
  inputProps={{ min: 1 }}
  sx={{ width: { xs: "100%", md: 180 }, flexShrink: 0 }}
/>
              <TextField label="Desconto (%)" value={proposta.descontoPercentual || ""} onChange={(event) => atualizarProposta("descontoPercentual", event.target.value)} sx={{ width: { xs: "100%", md: 160 }, flexShrink: 0 }} />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Destinatário" value={proposta.destinatario || ""} onChange={(event) => atualizarProposta("destinatario", event.target.value)} sx={{ flex: 1, minWidth: { md: 300 } }} />
              <TextField label="E-mail" value={proposta.email || ""} onChange={(event) => atualizarProposta("email", event.target.value)} sx={{ flex: 1, minWidth: { md: 300 } }} />
              <TextField label="Telefone" value={proposta.telefone || ""} onChange={(event) => atualizarProposta("telefone", event.target.value)} sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }} />
            </Stack>

            <TextField
              label="Título da proposta no relatório"
              value={proposta.tituloRelatorio || ""}
              onChange={(event) => atualizarProposta("tituloRelatorio", event.target.value)}
              placeholder="Proposta comercial"
              fullWidth
            />
          </Stack>
        </Paper>

        {(proposta.tabelas || []).map((grupo, grupoIndex) => {
          const subtotalGrupo = (grupo.itens || []).reduce((total, item) => total + linhaTotal(item), 0);
          return (
          <Paper key={grupo.id || grupoIndex} className="proposta-form-tabela" variant="outlined" sx={{ mt: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
              <TextField
                label="Grupo de serviços"
                value={grupo.titulo || `Grupo ${grupoIndex + 1}`}
                onChange={(event) => atualizarGrupo(grupoIndex, "titulo", event.target.value)}
                sx={{ width: { md: 260 } }}
              />
              <TextField
                label="Referência do grupo"
                value={grupo.subtitulo || ""}
                onChange={(event) => atualizarGrupo(grupoIndex, "subtitulo", event.target.value)}
                fullWidth
              />
            </Stack>

            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 58 }}>Item</TableCell>
                  <TableCell sx={{ width: 92 }}>Unid.</TableCell>
                  <TableCell sx={{ width: 82 }}>Qtd.</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell sx={{ width: 120 }}>Valor unit.</TableCell>
                  <TableCell sx={{ width: 105 }} align="right">Total</TableCell>
                  <TableCell sx={{ width: 56, px: 0.5 }} align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {(grupo.itens || []).map((item, itemIndex) => {
                  return (
                    <TableRow key={item.id || `${grupoIndex}-${itemIndex}`}>
                      <TableCell align="left">
                        <Typography fontWeight={800}>{`${grupoIndex + 1}.${itemIndex + 1}`}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 0.75 }}>
                        <TextField select size="small" value={item.unidade || ""} onChange={(event) => atualizarItem(grupoIndex, itemIndex, "unidade", event.target.value)} fullWidth>
                          <MenuItem value="UND">UND</MenuItem>
                          <MenuItem value="UN">UN</MenuItem>
                          <MenuItem value="MÊS">MÊS</MenuItem>
                          <MenuItem value="DIA">DIA</MenuItem>
                          <MenuItem value="HRS">HRS</MenuItem>
                          <MenuItem value="M2">M2</MenuItem>
                          <MenuItem value="M3">M3</MenuItem>
                          <MenuItem value="VB">VB</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" fullWidth value={item.quantidade || ""} onChange={(event) => atualizarItem(grupoIndex, itemIndex, "quantidade", event.target.value)} />
                      </TableCell>
                      <TableCell>
  <Stack spacing={1}>
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ flex: 1 }}>
        <ServicoAutocompleteDebounced
          item={item}
          opcoesServico={opcoesServico}
          onDescricaoChange={(valor) => atualizarItem(grupoIndex, itemIndex, "descricao", valor)}
          onServicoSelect={(valor) => selecionarServico(grupoIndex, itemIndex, valor)}
        />
      </Box>

      {(item.servicoId || item.descricao) && (
        <Button
          size="small"
          variant={item.detalhesServico ? "contained" : "outlined"}
          onClick={() => abrirDetalheServico(grupoIndex, itemIndex)}
          sx={{
            minWidth: 96,
            height: 40,
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {item.detalhesServico ? "Editar detalhe" : "+ Detalhes"}
        </Button>
      )}
    </Stack>

    {(detalhesAbertos[chaveDetalheServico(grupoIndex, itemIndex)] || item.detalhesServico) && (
      <TextField
        label="Detalhes do serviço"
        value={item.detalhesServico || ""}
        onChange={(event) =>
          atualizarItem(grupoIndex, itemIndex, "detalhesServico", event.target.value)
        }
        multiline
        minRows={2}
        size="small"
        fullWidth
        placeholder="Informe os detalhes específicos deste serviço"
        inputRef={(elemento) => {
          detalhesRefs.current[chaveDetalheServico(grupoIndex, itemIndex)] = elemento;
        }}
      />
    )}
  </Stack>
</TableCell>
                      <TableCell>
                        <ValorUnitarioInput
                          value={item.valorUnitario || ""}
                          onChange={(valor) => atualizarItem(grupoIndex, itemIndex, "valorUnitario", valor)}
                        />
                      </TableCell>
                      <TableCell align="right">{moeda(linhaTotal(item))}</TableCell>
                      <TableCell align="center" sx={{ px: 0.5 }}>
                        <Tooltip title="Excluir serviço">
                          <IconButton size="small" color="error" onClick={() => removerItem(grupoIndex, itemIndex)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {subtotalGrupo > 0 && <TableRow>
                  <TableCell colSpan={5} align="right" sx={{ fontWeight: 800 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                      <span>SUB-TOTAL: {valorMonetarioPorExtenso(subtotalGrupo)}</span>
                      <Button
                        size="small"
                        variant={grupo.exibirSubtotal !== false ? "outlined" : "contained"}
                        onClick={() => atualizarGrupo(grupoIndex, "exibirSubtotal", grupo.exibirSubtotal === false)}
                        sx={{ minWidth: 86, whiteSpace: "nowrap" }}
                      >
                        {grupo.exibirSubtotal !== false ? "Ocultar" : "Exibir"}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell colSpan={2} align="right" sx={{ fontWeight: 800 }}>
                    {moeda(subtotalGrupo)}
                  </TableCell>
                </TableRow>}
              </TableBody>
            </Table>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mt={1}>
              <Button startIcon={<AddIcon />} onClick={() => adicionarItem(grupoIndex)}>
                Adicionar serviço
              </Button>
              <Button variant="outlined" onClick={() => setOpenNovoServico(true)}>
                Cadastrar novo serviço
              </Button>
            </Stack>
          </Paper>
          );
        })}

        <Button variant="outlined" startIcon={<AddIcon />} onClick={adicionarGrupo} sx={{ mt: 2 }}>
          Adicionar grupo de serviços
        </Button>

        <Paper className="proposta-resumo" variant="outlined" sx={{ mt: 2 }}>
          <Typography fontWeight={800}>Resumo financeiro</Typography>
          <span>Total bruto: {moeda(totais.subtotal)}</span>
          <span>Desconto: {moeda(totais.desconto)}</span>
          <strong>Total final: {moeda(totais.total)}</strong>
        </Paper>

        <Paper className="proposta-form-tabela" variant="outlined" sx={{ mt: 2 }}>
          <Typography fontWeight={800} mb={1}>Textos da proposta</Typography>
          {Object.entries({
            saudacao: "Saudação",
            apresentacao: "Apresentação",
            observacoes: "Observações",
            prazoInicio: "Prazo de início",
            entregaRelatorios: "Entrega dos relatórios",
            pagamento: "Medição e pagamento",
          }).map(([campo, label]) => (
            <TextField
              key={campo}
              label={campo === "entregaRelatorios" ? (
                <span
                  contentEditable
                  suppressContentEditableWarning
                  role="textbox"
                  aria-label="Editar título da entrega"
                  title="Clique para editar o título"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onBlur={(event) => atualizarTexto(
                    "tituloEntrega",
                    event.currentTarget.textContent.trim() || "Entrega dos relatórios"
                  )}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      event.currentTarget.blur();
                    }
                  }}
                  style={{ cursor: "text", pointerEvents: "auto" }}
                >
                  {proposta.textos?.tituloEntrega || "Entrega dos relatórios"}
                </span>
              ) : label}
              InputLabelProps={campo === "entregaRelatorios" ? { shrink: true } : undefined}
              value={proposta.textos?.[campo] || ""}
              onChange={(event) => atualizarTexto(campo, event.target.value)}
              multiline={campo !== "saudacao"}
              minRows={campo === "saudacao" ? undefined : 2}
              fullWidth
              sx={{ mb: 2 }}
            />
          ))}
        </Paper>
      </Box>

      <Dialog open={openNovoServico} onClose={() => !salvandoCadastro && setOpenNovoServico(false)} fullWidth maxWidth="sm">
        <DialogTitle>Novo tipo de serviço</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField label="Serviço" value={novoServico.nome} onChange={(event) => setNovoServico({ ...novoServico, nome: event.target.value })} fullWidth />
            <TextField label="Norma de referência" value={novoServico.norma} onChange={(event) => setNovoServico({ ...novoServico, norma: event.target.value })} fullWidth />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Tipo" value={novoServico.tipo} onChange={(event) => setNovoServico({ ...novoServico, tipo: event.target.value.toUpperCase() })} fullWidth />
              <TextField label="Categoria" value={novoServico.categoria} onChange={(event) => setNovoServico({ ...novoServico, categoria: event.target.value.toUpperCase() })} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNovoServico(false)} disabled={salvandoCadastro}>Cancelar</Button>
          <Button variant="contained" onClick={() => executarSalvamento(salvarNovoServico)} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar serviço"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
